import { CartUpdatedEvent, ThemeEvents } from '@theme/events';
import { EpicPackBase } from '@theme/epic-pack-core';

/**
 * Epic Standard Pack
 *
 * Bloc d'achat des pages Box Standard (box déjà composée, non configurable).
 * Le moteur de prix (packs, supplément, Tower shiny, Loot seule, prix barré) vit dans
 * EpicPackBase (assets/epic-pack-core.js). Ici, spécificités du mode standard :
 *   - pas de colonnes Top/Token/Skin : la box est le produit de la page ;
 *   - relief fixe, lu depuis `data-is-relief` (metafield `custom.is_relief`) ;
 *   - `data-pack-type-value` = « Standard » (filtre des variantes pack) ;
 *   - sélecteur de variante box si plusieurs variantes (ex. couleurs Cthulhu).
 *
 * Une « selection » = this.config (sélection unique ; la quantité multiplie).
 * Le bouton d'achat est porté par ce bloc (remplace l'achat natif).
 */
class EpicStandardPack extends EpicPackBase {
  constructor() {
    super();
    this.box = { name: '', variants: [] };
    this.packs = { tower: { variants: [] }, full: { variants: [] } };
    this.options = { tower: { enabled: false, variants: [] }, lootbox: { enabled: false } };
    this.config = { box: '', tower: false, lootbox: false, towerColor: '' };
    this.quantity = 1;
    this.chevron = '';

    this.onAddToCart = this.onAddToCart.bind(this);
    this.onQuantityChange = this.onQuantityChange.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
    this.onVariantUpdated = this.onVariantUpdated.bind(this);
  }

  connectedCallback() {
    const dataEl = this.querySelector('[data-ref="epic-pack-data"]');
    if (!dataEl) return;

    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('Epic Standard Pack: données JSON invalides', e);
      return;
    }

    this.box = data.box || this.box;
    if (data.packs) this.packs = data.packs;
    if (data.options) this.options = data.options;
    this.chevron = this.querySelector('[data-ref="epic-chevron"]')?.innerHTML || '';

    if (!this.box.variants?.length) return;

    this.config.box = String(this.box.variants[0].id);
    const params = new URLSearchParams(window.location.search);
    // Mode variante native : initialise depuis ?variant=… si présent
    if (this.dataset.nativeVariant === 'true') {
      const variantParam = params.get('variant');
      if (variantParam && this.box.variants.some((v) => String(v.id) === variantParam)) {
        this.config.box = variantParam;
      }
    }
    this.config.tower = params.get('tower') === '1' && Boolean(this.options.tower?.enabled);
    this.config.lootbox = params.get('lootbox') === '1' && Boolean(this.options.lootbox?.enabled);
    if (this.options.lootbox?.requiresTower && !this.config.tower) this.config.lootbox = false;
    const towerVariants = this.options.tower?.variants || [];
    this.config.towerColor = towerVariants.length ? String(towerVariants[0].id) : '';

    this.container = this.querySelector('[data-ref="epic-pack-body"]');
    this.qtyInput = this.querySelector('[data-ref="epic-qty-input"]');
    this.addButton = this.querySelector('[data-ref="epic-add-to-cart"]');
    this.totalEl = this.querySelector('[data-ref="epic-total"]');
    this.quantity = Math.max(1, parseInt(this.qtyInput?.value, 10) || 1);

    this.render();
    this.updateTotal();

    this.qtyInput?.addEventListener('change', this.onQuantityChange);
    this.addButton?.addEventListener('click', this.onAddToCart);
    window.addEventListener('pageshow', this.onPageShow);
    // Mode variante native : on se synchronise sur le sélecteur de variante du thème
    if (this.dataset.nativeVariant === 'true') {
      document.addEventListener(ThemeEvents.variantUpdated, this.onVariantUpdated);
    }
  }

  disconnectedCallback() {
    this.qtyInput?.removeEventListener('change', this.onQuantityChange);
    this.addButton?.removeEventListener('click', this.onAddToCart);
    window.removeEventListener('pageshow', this.onPageShow);
    document.removeEventListener(ThemeEvents.variantUpdated, this.onVariantUpdated);
  }

  /** Sélecteur de variante natif du thème (variant:updated) → met à jour la box choisie. */
  onVariantUpdated(e) {
    const id = e.detail?.resource?.id;
    if (!id) return;
    const pid = e.detail?.data?.productId;
    if (this.dataset.productId && pid && String(pid) !== String(this.dataset.productId)) return;
    const idStr = String(id);
    if (!this.box.variants.some((v) => String(v.id) === idStr)) return;
    this.config.box = idStr;
    this.updateTotal();
  }

  onPageShow() {
    if (!this.container) return;
    const boxSelect = this.container.querySelector('[data-ref="epic-box-select"]');
    if (boxSelect && boxSelect.value) this.config.box = boxSelect.value;
    const towerSelect = this.container.querySelector('[data-ref="epic-tower-select"]');
    if (towerSelect && towerSelect.value) this.config.towerColor = towerSelect.value;
    this.updateTotal();
  }

  onQuantityChange() {
    this.quantity = Math.max(1, parseInt(this.qtyInput.value, 10) || 1);
    this.updateTotal();
  }

  // ── Hooks du moteur de prix (EpicPackBase) ───────────────────────────────────

  isPremium() {
    return this.dataset.isRelief === 'true';
  }

  baseVariant() {
    return this.selectedBoxVariant();
  }

  towerVariant() {
    const variants = this.options.tower?.variants || [];
    if (!variants.length) return null;
    return (
      variants.find((v) => String(v.id) === String(this.config.towerColor)) || variants[0]
    );
  }

  optionState() {
    return { tower: Boolean(this.config.tower), lootbox: Boolean(this.config.lootbox) };
  }

  selectedBoxVariant() {
    return (
      this.box.variants.find((v) => String(v.id) === String(this.config.box)) ||
      this.box.variants[0]
    );
  }

  /**
   * Variante box réellement sélectionnée à l'instant T (source synchrone).
   *  - mode natif : l'URL ?variant=… (mise à jour sync par le variant-picker du thème) ;
   *  - mode interne : la valeur du select.
   * Évite le décalage d'un cran de l'événement variant:updated (async).
   */
  currentBoxId() {
    if (this.dataset.nativeVariant === 'true') {
      const vp = new URLSearchParams(window.location.search).get('variant');
      if (vp && this.box.variants.some((v) => String(v.id) === vp)) return vp;
    } else {
      const sel = this.container?.querySelector('[data-ref="epic-box-select"]');
      if (sel && sel.value) return sel.value;
    }
    return this.config.box;
  }

  // ── Rendu ──────────────────────────────────────────────────────────────────

  render() {
    this.container.innerHTML = '';
    // En mode variante native, le sélecteur du thème gère le choix → on n'affiche pas le nôtre
    if (this.box.variants.length > 1 && this.dataset.nativeVariant !== 'true') {
      this.container.appendChild(this.buildBoxSelector());
    }
    const opts = this.buildOptionsRow();
    if (opts) this.container.appendChild(opts);
  }

  /** Sélecteur de variante de la box (ex. couleur Cthulhu) — seulement si > 1 variante. */
  buildBoxSelector() {
    const wrap = document.createElement('div');
    wrap.className = 'epic-pack-box';

    const selected = this.selectedBoxVariant();
    const img = document.createElement('img');
    img.className = 'epic-pack-box__image';
    img.dataset.ref = 'epic-box-image';
    img.width = 64;
    img.height = 64;
    img.loading = 'lazy';
    img.alt = this.box.name || '';
    if (selected?.image) img.src = selected.image;
    else img.classList.add('epic-pack-box__image--empty');
    wrap.appendChild(img);

    const label = document.createElement('span');
    label.className = 'epic-pack-box__label';
    label.textContent = this.dataset.boxColorLabel || 'Couleur';
    wrap.appendChild(label);

    const selectWrap = document.createElement('div');
    selectWrap.className = 'select-wrapper';
    const select = document.createElement('select');
    select.className = 'select';
    select.dataset.ref = 'epic-box-select';
    select.autocomplete = 'off';
    select.setAttribute('aria-label', label.textContent);
    this.box.variants.forEach((v) => {
      const option = document.createElement('option');
      option.value = String(v.id);
      option.textContent = v.title;
      if (v.image) option.dataset.variantImage = v.image;
      if (!v.available) option.textContent = `${v.title} (épuisé)`;
      if (String(v.id) === String(this.config.box)) option.selected = true;
      select.appendChild(option);
    });
    if (this.config.box) select.value = String(this.config.box);
    select.addEventListener('change', (e) => this.onBoxChange(e.currentTarget, img));
    selectWrap.appendChild(select);

    const iconWrap = document.createElement('div');
    iconWrap.className = 'select-wrapper__icon';
    iconWrap.innerHTML = this.chevron;
    selectWrap.appendChild(iconWrap);

    wrap.appendChild(selectWrap);
    return wrap;
  }

  onBoxChange(select, img) {
    this.config.box = select.value;
    const option = select.options[select.selectedIndex];
    const vimg = option?.dataset.variantImage;
    if (vimg && img) {
      img.src = vimg;
      img.classList.remove('epic-pack-box__image--empty');
    }
    this.updateTotal();
  }

  buildOptionsRow() {
    const tower = this.options.tower;
    const loot = this.options.lootbox;
    const towerOn = tower?.enabled && this.packs.tower?.variants?.length > 0;
    const lootOn = loot?.enabled && this.packs.full?.variants?.length > 0;
    if (!towerOn && !lootOn) return null;

    const wrap = document.createElement('div');
    wrap.className = 'epic-options';

    if (this.dataset.optionsTitle) {
      const title = document.createElement('p');
      title.className = 'epic-options__title variant_label';
      title.textContent = this.dataset.optionsTitle;
      wrap.appendChild(title);
    }

    if (towerOn) {
      wrap.appendChild(this.buildOptionToggle('tower', tower));
      if (this.config.tower) {
        const colorRow = this.buildTowerColorRow();
        if (colorRow) wrap.appendChild(colorRow);
      }
    }
    if (lootOn) wrap.appendChild(this.buildOptionToggle('lootbox', loot));
    return wrap;
  }

  buildOptionToggle(key, opt) {
    const row = document.createElement('label');
    row.className = 'epic-option';
    row.dataset.optionKey = key;

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'epic-option__checkbox';
    checkbox.checked = Boolean(this.config[key]);

    if (key === 'lootbox' && opt.requiresTower && !this.config.tower) {
      checkbox.checked = false;
      checkbox.disabled = true;
      row.classList.add('epic-option--disabled');
    }

    checkbox.addEventListener('change', () => this.onOptionChange(key, checkbox.checked));
    row.appendChild(checkbox);

    if (opt.image) {
      const img = document.createElement('img');
      img.className = 'epic-option__image';
      img.src = opt.image;
      img.alt = opt.label || key;
      img.width = 40;
      img.height = 40;
      img.loading = 'lazy';
      row.appendChild(img);
    }

    const label = document.createElement('span');
    label.className = 'epic-option__label';
    label.textContent = opt.label || key;
    row.appendChild(label);

    const tag = this.buildPriceTag(this.optionPricing(this.config, key));
    if (tag) row.appendChild(tag);

    return row;
  }

  buildTowerColorRow() {
    const variants = this.options.tower?.variants || [];
    if (variants.length < 2) return null;

    const wrap = document.createElement('div');
    wrap.className = 'epic-tower-color';

    const label = document.createElement('span');
    label.className = 'epic-tower-color__label';
    label.textContent = this.dataset.towerColorLabel || 'Couleur de la Tower';
    wrap.appendChild(label);

    const selectWrap = document.createElement('div');
    selectWrap.className = 'select-wrapper';
    const select = document.createElement('select');
    select.className = 'select';
    select.dataset.ref = 'epic-tower-select';
    select.autocomplete = 'off';
    select.setAttribute('aria-label', label.textContent);
    variants.forEach((v) => {
      const option = document.createElement('option');
      option.value = String(v.id);
      option.textContent = v.title;
      if (String(v.id) === String(this.config.towerColor)) option.selected = true;
      select.appendChild(option);
    });
    if (this.config.towerColor) select.value = String(this.config.towerColor);
    select.addEventListener('change', (e) => {
      this.config.towerColor = e.currentTarget.value;
      this.refreshOptions();
      this.updateTotal();
    });
    selectWrap.appendChild(select);

    const iconWrap = document.createElement('div');
    iconWrap.className = 'select-wrapper__icon';
    iconWrap.innerHTML = this.chevron;
    selectWrap.appendChild(iconWrap);

    wrap.appendChild(selectWrap);
    return wrap;
  }

  onOptionChange(key, checked) {
    this.config[key] = checked;
    if (key === 'tower' && !checked && this.options.lootbox?.requiresTower) {
      this.config.lootbox = false;
    }
    this.refreshOptions();
    this.updateTotal();
  }

  refreshOptions() {
    const oldRow = this.container.querySelector('.epic-options');
    const newRow = this.buildOptionsRow();
    if (oldRow && newRow) oldRow.replaceWith(newRow);
    else if (oldRow && !newRow) oldRow.remove();
  }

  updateTotal() {
    this.renderTotal(
      this.unitTotalPrice(this.config) * this.quantity,
      this.unitFullPrice(this.config) * this.quantity,
    );
  }

  /**
   * Total rendu DANS le bouton, façon ATC natif : prix payé + prix barré (plein).
   * Surcharge le renderTotal de EpicPackBase pour réutiliser les classes du thème.
   */
  renderTotal(promoCents, fullCents) {
    if (!this.totalEl) return;
    this.totalEl.textContent = '';
    const price = document.createElement('span');
    price.className = 'price';
    price.textContent = this.formatPrice(promoCents);
    this.totalEl.appendChild(price);
    if (fullCents > promoCents) {
      const cmp = document.createElement('span');
      cmp.className = 'compare-at-price';
      cmp.textContent = this.formatPrice(fullCents);
      this.totalEl.appendChild(cmp);
    }
  }

  /** Libellé d'identité de la box (nom + couleur si plusieurs variantes). */
  boxPropertyValue() {
    const name = this.dataset.boxName || this.box.name || 'Box';
    const v = this.selectedBoxVariant();
    if (this.box.variants.length > 1 && v && v.title && v.title !== 'Default Title') {
      return `${name} — ${v.title}`;
    }
    return name;
  }

  // ── Panier ─────────────────────────────────────────────────────────────────

  async onAddToCart() {
    // Re-synchronise la variante box au clic (source synchrone) → pas de décalage d'un cran
    this.config.box = this.currentBoxId();
    this.updateTotal();
    const main = this.resolveMainVariant(this.config);
    if (!main) return;

    this.addButton.classList.add('is-loading');
    this.addButton.disabled = true;

    const items = [];
    const boxUid = Date.now().toString(36);
    const props = {};

    if (this.config.tower) {
      props[this.dataset.boxLabel || 'Box'] = this.boxPropertyValue();
      const tv = this.towerVariant(this.config);
      props[this.options.tower?.label || 'Epic Tower'] = tv ? tv.title : '';
      if (this.config.lootbox) {
        props[this.options.lootbox?.label || 'Loot Box'] =
          this.options.lootbox?.versionLabel || 'V2';
      }
      props._box = boxUid;
    }

    items.push({
      id: main.id,
      quantity: this.quantity,
      properties: Object.keys(props).length ? props : undefined,
    });

    // Loot Box SEULE (sans Tower) : ligne séparée au plein tarif.
    if (this.config.lootbox && !this.config.tower && this.options.lootbox?.addVariantId) {
      items.push({
        id: this.options.lootbox.addVariantId,
        quantity: this.quantity,
        properties: { _box: boxUid },
      });
    }

    const sections = [];
    const drawer = document.querySelector('cart-drawer');
    if (drawer) sections.push(drawer.dataset.sectionId);

    const cartAddUrl =
      (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(',') }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Ajout au panier impossible');
      }

      const json = await response.json();
      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            source: 'epic-standard-pack',
            itemCount: json.item_count,
            sections: json.sections,
          },
        ),
      );
      document.dispatchEvent(new CustomEvent(ThemeEvents.cartOpen));
    } catch (error) {
      console.error('Epic Standard Pack: ajout panier échoué', error);
      document.dispatchEvent(
        new CustomEvent('toast:open', {
          detail: { type: 'error', message: error.message },
        }),
      );
    } finally {
      this.addButton.classList.remove('is-loading');
      this.addButton.disabled = false;
    }
  }
}

if (!customElements.get('epic-standard-pack')) {
  customElements.define('epic-standard-pack', EpicStandardPack);
}
